import React, { useState, useEffect, useCallback } from 'react';
import { History, RefreshCw, ChevronLeft, ChevronRight } from 'lucide-react';
import toast from 'react-hot-toast';
import { adminAPI } from '../../services/api';
import PageHeader from '../../components/UI/PageHeader';
import Card from '../../components/UI/Card';
import Button from '../../components/UI/Button';
import StatusBadge from '../../components/UI/StatusBadge';

const ACTION_LABELS = {
  login: 'Login',
  created: 'Session Created',
  acknowledged: 'Acknowledged',
  initiated: 'Initiated',
  paused: 'Paused',
  resumed: 'Resumed',
  cancelled: 'Cancelled',
  system_reset: 'System Reset',
  request_submitted: 'Request Submitted',
  request_approved: 'Request Approved',
  request_rejected: 'Request Rejected',
  template_request_submitted: 'Template Request Submitted',
  template_request_approved: 'Template Request Approved',
  template_request_rejected: 'Template Request Rejected',
  template_request_revision_requested: 'Template Revision Requested',
};

const PAGE_SIZE = 50;

export default function AdminAuditLogPage() {
  const [rows, setRows] = useState([]);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(0);
  const [action, setAction] = useState('');
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await adminAPI.getAuditLog({
        limit: PAGE_SIZE,
        offset: page * PAGE_SIZE,
        action: action || undefined,
      });
      setRows(res.data.data || []);
      setTotal(res.data.total || 0);
    } catch {
      toast.error('Failed to load audit log');
    } finally {
      setLoading(false);
    }
  }, [page, action]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="max-w-5xl mx-auto px-4 py-6">
      <PageHeader
        icon={History}
        title="Audit Log"
        subtitle="Every significant operational action (CR Section 13)"
        actions={<Button variant="ghost" size="sm" icon={RefreshCw} onClick={load} />}
      />

      <div className="mb-4">
        <select
          value={action}
          onChange={e => { setAction(e.target.value); setPage(0); }}
          className="border border-gray-300 rounded-xl px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
        >
          <option value="">All actions</option>
          {Object.entries(ACTION_LABELS).map(([value, label]) => (
            <option key={value} value={value}>{label}</option>
          ))}
        </select>
      </div>

      <Card padded={false} className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 border-b border-gray-200">
              <tr>
                <th className="text-left px-4 py-2.5 font-semibold text-gray-500">When</th>
                <th className="text-left px-4 py-2.5 font-semibold text-gray-500">User</th>
                <th className="text-left px-4 py-2.5 font-semibold text-gray-500">Action</th>
                <th className="text-left px-4 py-2.5 font-semibold text-gray-500">Flight / Gate</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100">
              {loading ? (
                <tr><td colSpan={4} className="px-4 py-8 text-center text-gray-400">Loading…</td></tr>
              ) : rows.length === 0 ? (
                <tr><td colSpan={4} className="px-4 py-8 text-center text-gray-400">No entries</td></tr>
              ) : rows.map(r => (
                <tr key={r.id} className="hover:bg-gray-50">
                  <td className="px-4 py-2.5 text-gray-500 whitespace-nowrap">
                    {new Date(r.createdAt).toLocaleString([], { dateStyle: 'medium', timeStyle: 'short' })}
                  </td>
                  <td className="px-4 py-2.5">
                    <span className="font-medium text-gray-800">{r.user?.name || '—'}</span>
                    <span className="text-xs text-gray-400 ml-1">({r.user?.role})</span>
                  </td>
                  <td className="px-4 py-2.5">
                    <StatusBadge label={ACTION_LABELS[r.action] || r.action} tone="info" />
                  </td>
                  <td className="px-4 py-2.5 text-gray-500">
                    {r.session?.flight_number || '—'}
                    {r.gate?.gate_code ? ` · ${r.gate.gate_code}` : ''}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>

      <div className="flex items-center justify-between mt-3 text-sm text-gray-500">
        <span>{total} total entries</span>
        <div className="flex items-center gap-2">
          <Button variant="ghost" size="sm" icon={ChevronLeft} onClick={() => setPage(p => Math.max(0, p - 1))} disabled={page === 0} />
          <span>Page {page + 1}</span>
          <Button variant="ghost" size="sm" icon={ChevronRight} onClick={() => setPage(p => (p + 1) * PAGE_SIZE < total ? p + 1 : p)} disabled={(page + 1) * PAGE_SIZE >= total} />
        </div>
      </div>
    </div>
  );
}
